import {
    type ComponentProps,
    type KeyboardEvent,
    type ReactElement,
    type ReactNode,
    type RefObject,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    type EmojiCatalogEntry,
    type EmojiShortcodeToken,
    findEmojiShortcodeToken,
    findExactEmojiShortcode,
    loadEmojiCatalog,
    replaceTextRange,
    searchEmojiCatalog,
} from '@apps/web/components/shared/messages/emoji_catalog';
import { MessageEmojiPicker } from '@apps/web/components/shared/messages/message_emoji_picker';
import { Textarea } from '@apps/web/components/ui/textarea';
import { cn } from '@apps/web/lib/utils';

type SelectionRange = {
    readonly end: number;
    readonly start: number;
};

type UseEmojiComposerOptions = {
    readonly content: string;
    readonly maxLength: number;
    readonly onContentChange: (content: string) => void;
    readonly onInsertionLimit: () => void;
    readonly textareaRef: RefObject<HTMLTextAreaElement | null>;
};

type EmojiComposerSupport = {
    readonly activeSuggestionHexcode: string | null;
    readonly applyEmoji: (unicode: string) => void;
    readonly autocompleteExpanded: boolean;
    readonly autocompleteId: string;
    readonly handleKeyDown: (
        event: KeyboardEvent<HTMLTextAreaElement>,
    ) => boolean;
    readonly rememberSelection: (textarea: HTMLTextAreaElement) => void;
    readonly reset: () => void;
    readonly selectSuggestion: (entry: EmojiCatalogEntry) => void;
    readonly onActiveSuggestionChange: (hexcode: string) => void;
    readonly suggestions: ReadonlyArray<EmojiCatalogEntry>;
    readonly updateDraftSelection: (value: string, cursor: number) => void;
};

export function resizeMessageTextarea(
    textarea: HTMLTextAreaElement | null,
): void {
    if (!textarea) {
        return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
}

/** Keeps native textarea selection intact while an emoji control has focus. */
function useEmojiComposer({
    content,
    maxLength,
    onContentChange,
    onInsertionLimit,
    textareaRef,
}: UseEmojiComposerOptions): EmojiComposerSupport {
    const [catalog, setCatalog] = useState<
        ReadonlyArray<EmojiCatalogEntry> | undefined
    >();
    const [catalogLoadFailed, setCatalogLoadFailed] = useState(false);
    const selectionRef = useRef<SelectionRange>({
        end: content.length,
        start: content.length,
    });
    const [shortcodeToken, setShortcodeToken] =
        useState<EmojiShortcodeToken | null>(null);
    const [pendingCompletedToken, setPendingCompletedToken] =
        useState<EmojiShortcodeToken | null>(null);
    const [dismissed, setDismissed] = useState(false);
    const [requestedActiveSuggestionHexcode, setActiveSuggestionHexcode] =
        useState<string | null>(null);
    const autocompleteId = `message-emoji-shortcode-${useId()}`;

    const saveSelection = useCallback((nextSelection: SelectionRange) => {
        selectionRef.current = nextSelection;
    }, []);

    useEffect(() => {
        if (
            (shortcodeToken === null && pendingCompletedToken === null) ||
            catalog !== undefined ||
            catalogLoadFailed
        ) {
            return;
        }
        let active = true;
        void loadEmojiCatalog()
            .then((loadedCatalog) => {
                if (active) {
                    setCatalog(loadedCatalog);
                }
            })
            .catch((error: unknown) => {
                console.error('Failed to load emoji autocomplete', error);
                if (active) {
                    setCatalogLoadFailed(true);
                }
            });
        return () => {
            active = false;
        };
    }, [catalog, catalogLoadFailed, pendingCompletedToken, shortcodeToken]);

    const insertAtRange = useCallback(
        (unicode: string, range: SelectionRange) => {
            const replacement = replaceTextRange(
                content,
                range.start,
                range.end,
                unicode,
                maxLength,
            );
            if (replacement === null) {
                onInsertionLimit();
                return;
            }

            onContentChange(replacement.value);
            setShortcodeToken(null);
            setPendingCompletedToken(null);
            setDismissed(false);
            saveSelection({
                end: replacement.cursor,
                start: replacement.cursor,
            });
            window.requestAnimationFrame(() => {
                const textarea = textareaRef.current;
                if (!textarea) {
                    return;
                }
                textarea.focus();
                textarea.setSelectionRange(
                    replacement.cursor,
                    replacement.cursor,
                );
                resizeMessageTextarea(textarea);
            });
        },
        [
            content,
            maxLength,
            onContentChange,
            onInsertionLimit,
            saveSelection,
            textareaRef,
        ],
    );

    useEffect(() => {
        if (!pendingCompletedToken || catalog === undefined) {
            return;
        }
        const expectedToken = `:${pendingCompletedToken.query}:`;
        if (
            content.slice(
                pendingCompletedToken.start,
                pendingCompletedToken.end,
            ) !== expectedToken
        ) {
            setPendingCompletedToken(null);
            return;
        }
        const exactMatch = findExactEmojiShortcode(
            catalog,
            pendingCompletedToken.query,
        );
        if (!exactMatch) {
            setShortcodeToken(null);
            setPendingCompletedToken(null);
            return;
        }
        insertAtRange(exactMatch.unicode, pendingCompletedToken);
    }, [catalog, content, insertAtRange, pendingCompletedToken]);

    const activeShortcodeToken =
        dismissed ||
        shortcodeToken === null ||
        shortcodeToken.complete ||
        !shortcodeToken.query
            ? null
            : shortcodeToken;
    const suggestions = useMemo(() => {
        if (activeShortcodeToken === null || catalog === undefined) {
            return [];
        }
        return searchEmojiCatalog(catalog, activeShortcodeToken.query, 8);
    }, [activeShortcodeToken, catalog]);

    const activeSuggestion =
        suggestions.find(
            (entry) => entry.hexcode === requestedActiveSuggestionHexcode,
        ) ?? suggestions[0];
    const activeSuggestionHexcode = activeSuggestion?.hexcode ?? null;

    const updateDraftSelection = useCallback(
        (value: string, cursor: number) => {
            saveSelection({ end: cursor, start: cursor });
            const nextToken = findEmojiShortcodeToken(value, cursor);
            if (
                nextToken &&
                (shortcodeToken === null ||
                    shortcodeToken.start !== nextToken.start)
            ) {
                setCatalogLoadFailed(false);
            }
            setShortcodeToken(nextToken);
            if (nextToken?.complete) {
                setPendingCompletedToken(nextToken);
            }
            setDismissed(false);
            setActiveSuggestionHexcode(null);
        },
        [saveSelection, shortcodeToken],
    );

    const rememberSelection = useCallback(
        (textarea: HTMLTextAreaElement) => {
            saveSelection({
                end: textarea.selectionEnd,
                start: textarea.selectionStart,
            });
        },
        [saveSelection],
    );

    const applyEmoji = useCallback(
        (unicode: string) => {
            insertAtRange(unicode, selectionRef.current);
        },
        [insertAtRange],
    );

    const selectSuggestion = useCallback(
        (entry: EmojiCatalogEntry) => {
            if (!shortcodeToken) {
                return;
            }
            insertAtRange(entry.unicode, shortcodeToken);
        },
        [insertAtRange, shortcodeToken],
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement>): boolean => {
            if (activeShortcodeToken === null) {
                return false;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                setDismissed(true);
                return true;
            }

            if (suggestions.length === 0) {
                return false;
            }

            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const activeIndex = Math.max(
                    0,
                    suggestions.findIndex(
                        (entry) => entry.hexcode === activeSuggestionHexcode,
                    ),
                );
                const offset = event.key === 'ArrowDown' ? 1 : -1;
                const nextIndex =
                    (activeIndex + offset + suggestions.length) %
                    suggestions.length;
                setActiveSuggestionHexcode(
                    suggestions[nextIndex]?.hexcode ?? null,
                );
                return true;
            }

            if (
                (event.key === 'Enter' || event.key === 'Tab') &&
                !event.shiftKey &&
                activeSuggestion
            ) {
                event.preventDefault();
                selectSuggestion(activeSuggestion);
                return true;
            }

            return false;
        },
        [
            activeSuggestion,
            activeSuggestionHexcode,
            activeShortcodeToken,
            selectSuggestion,
            suggestions,
        ],
    );

    const reset = useCallback(() => {
        const nextSelection = { end: 0, start: 0 };
        selectionRef.current = nextSelection;
        setShortcodeToken(null);
        setPendingCompletedToken(null);
        setDismissed(false);
        setActiveSuggestionHexcode(null);
    }, []);

    return {
        activeSuggestionHexcode,
        applyEmoji,
        autocompleteExpanded: suggestions.length > 0,
        autocompleteId,
        handleKeyDown,
        rememberSelection,
        reset,
        selectSuggestion,
        onActiveSuggestionChange: setActiveSuggestionHexcode,
        suggestions,
        updateDraftSelection,
    };
}

function EmojiShortcodeSuggestions({
    activeSuggestionHexcode,
    id,
    onActiveSuggestionChange,
    onSelect,
    suggestions,
}: {
    readonly activeSuggestionHexcode: string | null;
    readonly id: string;
    readonly onActiveSuggestionChange: (hexcode: string) => void;
    readonly onSelect: (entry: EmojiCatalogEntry) => void;
    readonly suggestions: ReadonlyArray<EmojiCatalogEntry>;
}): ReactElement | null {
    if (suggestions.length === 0) {
        return null;
    }

    return (
        <div
            aria-label="Emoji suggestions"
            className="bg-popover text-popover-foreground absolute right-0 bottom-[calc(100%+0.5rem)] left-0 z-50 max-h-72 overflow-y-auto rounded-md border p-1 shadow-md md:right-auto md:w-80"
            data-testid="emoji-shortcode-suggestions"
            id={id}
            role="listbox"
        >
            {suggestions.map((entry) => {
                const active = entry.hexcode === activeSuggestionHexcode;
                const shortcode = entry.shortcodes[0];
                return (
                    <button
                        aria-label={`${entry.label}, :${shortcode}:`}
                        aria-selected={active}
                        className={cn(
                            'flex min-h-10 w-full items-center gap-3 rounded-sm px-2 py-1.5 text-left text-sm',
                            active ? 'bg-accent' : 'hover:bg-accent/70',
                        )}
                        id={`${id}-${entry.hexcode}`}
                        key={entry.hexcode}
                        onClick={() => onSelect(entry)}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() =>
                            onActiveSuggestionChange(entry.hexcode)
                        }
                        role="option"
                        type="button"
                    >
                        <span aria-hidden={true} className="text-xl">
                            {entry.unicode}
                        </span>
                        <span className="min-w-0">
                            <span className="block font-medium">
                                :{shortcode}:
                            </span>
                            <span className="text-muted-foreground block truncate text-xs">
                                {entry.label}
                            </span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

type ManagedTextareaProps = Omit<
    ComponentProps<typeof Textarea>,
    | 'aria-activedescendant'
    | 'aria-autocomplete'
    | 'aria-controls'
    | 'aria-expanded'
    | 'aria-haspopup'
    | 'maxLength'
    | 'onBlur'
    | 'onChange'
    | 'onClick'
    | 'onKeyDown'
    | 'onKeyUp'
    | 'onSelect'
    | 'ref'
    | 'role'
    | 'value'
>;

type EmojiEnabledTextareaRenderProps = {
    readonly emojiPicker: ReactElement;
    readonly textarea: ReactElement;
};

type EmojiEnabledTextareaProps = {
    readonly children: (controls: EmojiEnabledTextareaRenderProps) => ReactNode;
    readonly className?: string;
    readonly content: string;
    readonly maxLength: number;
    readonly onContentChange: (content: string) => void;
    readonly onInsertionLimit: () => void;
    readonly onInteraction?: () => void;
    readonly onTextareaKeyDown?: (
        event: KeyboardEvent<HTMLTextAreaElement>,
    ) => void;
    readonly pickerDisabled: boolean;
    readonly resetKey?: unknown;
    readonly textareaProps?: ManagedTextareaProps;
    readonly textareaRef: RefObject<HTMLTextAreaElement | null>;
};

/** Owns the shared shortcode, picker, selection, and combobox interaction. */
export function EmojiEnabledTextarea({
    children,
    className,
    content,
    maxLength,
    onContentChange,
    onInsertionLimit,
    onInteraction,
    onTextareaKeyDown,
    pickerDisabled,
    resetKey,
    textareaProps,
    textareaRef,
}: EmojiEnabledTextareaProps): ReactElement {
    const emojiComposer = useEmojiComposer({
        content,
        maxLength,
        onContentChange,
        onInsertionLimit,
        textareaRef,
    });
    const { reset } = emojiComposer;

    useEffect(() => {
        reset();
    }, [reset, resetKey]);

    useEffect(() => {
        if (!content) {
            reset();
        }
    }, [content, reset]);

    const textarea = (
        <Textarea
            {...textareaProps}
            aria-activedescendant={
                emojiComposer.autocompleteExpanded &&
                emojiComposer.activeSuggestionHexcode
                    ? `${emojiComposer.autocompleteId}-${emojiComposer.activeSuggestionHexcode}`
                    : undefined
            }
            aria-autocomplete="list"
            aria-controls={
                emojiComposer.autocompleteExpanded
                    ? emojiComposer.autocompleteId
                    : undefined
            }
            aria-expanded={emojiComposer.autocompleteExpanded}
            aria-haspopup="listbox"
            maxLength={maxLength}
            onBlur={(event) => {
                emojiComposer.rememberSelection(event.currentTarget);
            }}
            onChange={(event) => {
                onInteraction?.();
                onContentChange(event.target.value);
                emojiComposer.updateDraftSelection(
                    event.target.value,
                    event.target.selectionStart,
                );
            }}
            onClick={(event) => {
                emojiComposer.rememberSelection(event.currentTarget);
            }}
            onKeyDown={(event) => {
                if (!emojiComposer.handleKeyDown(event)) {
                    onTextareaKeyDown?.(event);
                }
            }}
            onKeyUp={(event) => {
                emojiComposer.rememberSelection(event.currentTarget);
            }}
            onSelect={(event) => {
                emojiComposer.rememberSelection(event.currentTarget);
            }}
            ref={textareaRef}
            role="combobox"
            value={content}
        />
    );
    const emojiPicker = (
        <MessageEmojiPicker
            disabled={pickerDisabled}
            onBeforeOpen={() => {
                const textareaElement = textareaRef.current;
                if (textareaElement) {
                    emojiComposer.rememberSelection(textareaElement);
                }
            }}
            onSelect={(unicode) => {
                onInteraction?.();
                emojiComposer.applyEmoji(unicode);
            }}
        />
    );

    return (
        <div className={className}>
            <EmojiShortcodeSuggestions
                activeSuggestionHexcode={emojiComposer.activeSuggestionHexcode}
                id={emojiComposer.autocompleteId}
                onActiveSuggestionChange={
                    emojiComposer.onActiveSuggestionChange
                }
                onSelect={(entry) => {
                    onInteraction?.();
                    emojiComposer.selectSuggestion(entry);
                }}
                suggestions={emojiComposer.suggestions}
            />
            {children({ emojiPicker, textarea })}
        </div>
    );
}
